/**
 * Parse a Google Maps URL for latitude and longitude.
 * Supports: @lat,lng in path, ?q=lat,lng, ?query=lat,lng, ?ll=lat,lng
 * @param {string} url - Full Google Maps URL
 * @returns {{ lat: number, lng: number } | null} - Parsed coords or null
 */
export function parseGoogleMapsLatLng(url) {
  if (!url || typeof url !== 'string') return null
  const trimmed = url.trim()
  if (!trimmed) return null

  // @lat,lng or @lat,lng,zoom (e.g. .../place/Name/@40.7128,-74.006,17z)
  const atMatch = trimmed.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/)
  if (atMatch) {
    const lat = parseFloat(atMatch[1])
    const lng = parseFloat(atMatch[2])
    if (isValidLat(lat) && isValidLng(lng)) return { lat, lng }
  }

  try {
    const urlObj = new URL(trimmed)
    const candidates = [
      urlObj.searchParams.get('q'),
      urlObj.searchParams.get('query'),
      urlObj.searchParams.get('ll'),
      urlObj.searchParams.get('center'),
    ]
    for (const raw of candidates) {
      if (!raw) continue
      let decoded = raw.replace(/\+/g, ' ').trim()
      try {
        decoded = decodeURIComponent(decoded)
      } catch (_) {
        // keep partially decoded
      }
      decoded = decoded.trim()
      const parts = decoded.split(/[,\s]+/).filter(Boolean)
      if (parts.length >= 2) {
        const lat = parseFloat(parts[0])
        const lng = parseFloat(parts[1])
        if (isValidLat(lat) && isValidLng(lng)) return { lat, lng }
      }
    }
  } catch (_) {
    // invalid URL
  }

  // Apple Maps share links (sometimes pasted into the map URL field)
  if (/maps\.apple\.com/i.test(trimmed)) {
    try {
      const u = new URL(trimmed)
      const ll = u.searchParams.get('ll') || u.searchParams.get('q')
      if (ll) {
        const parts = ll.split(/[,\s]+/).filter(Boolean)
        if (parts.length >= 2) {
          const lat = parseFloat(parts[0])
          const lng = parseFloat(parts[1])
          if (isValidLat(lat) && isValidLng(lng)) return { lat, lng }
        }
      }
    } catch (_) {
      // ignore
    }
  }

  return null
}

/**
 * OpenStreetMap external link (same coordinates as Leaflet map).
 * @param {number} lat
 * @param {number} lng
 * @param {number} [zoom=15]
 * @returns {string}
 */
export function openStreetMapUrl(lat, lng, zoom = 15) {
  const z = Number(zoom)
  const zClamped = Number.isFinite(z) ? Math.min(19, Math.max(1, Math.round(z))) : 15
  return `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lng}#map=${zClamped}/${lat}/${lng}`
}

function parseStoredLatLng(latRaw, lngRaw) {
  if (latRaw == null || lngRaw == null) return null
  const lat = Number(latRaw)
  const lng = Number(lngRaw)
  if (isValidLat(lat) && isValidLng(lng)) return { lat, lng }
  return null
}

/**
 * Coordinates for the public map: saved lat/lng first, else parsed from google_maps_url.
 * @param {object} restaurant - { latitude?, longitude?, google_maps_url? }
 * @returns {{ lat: number, lng: number } | null}
 */
export function getRestaurantMapCoordinates(restaurant) {
  if (!restaurant || typeof restaurant !== 'object') return null
  const fromFields = parseStoredLatLng(restaurant.latitude, restaurant.longitude)
  if (fromFields) return fromFields
  const url = restaurant.google_maps_url
  if (url && typeof url === 'string') return parseGoogleMapsLatLng(url)
  return null
}

/**
 * Whether the public site should show the map section (Leaflet and/or fallback link).
 * True if we have coordinates (stored or parsed from URL) or a saved map URL string.
 * @param {object} restaurant
 * @returns {boolean}
 */
export function restaurantHasMapOrLink(restaurant) {
  if (!restaurant || typeof restaurant !== 'object') return false
  if (getRestaurantMapCoordinates(restaurant)) return true
  const u = restaurant.google_maps_url
  return !!(u && typeof u === 'string' && u.trim())
}

function isValidLat(lat) {
  return typeof lat === 'number' && !Number.isNaN(lat) && lat >= -90 && lat <= 90
}

function isValidLng(lng) {
  return typeof lng === 'number' && !Number.isNaN(lng) && lng >= -180 && lng <= 180
}
